import React, { useState, useEffect, useRef, useMemo, useId } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass,
    faPlus,
    faXmark,
    faTag,
    faSpinner,
    faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import * as TagService from '../services/TagService.mjs';
import {
    indexTagsById,
    getAncestorPathLabel,
    getFullPathLabel,
    hasMultipleParents,
} from '../utils/tagHierarchy';
import './TagSelector.css';

// Cap the rendered list so a workspace with thousands of tags never paints them
// all at once. Results are relevance-ranked first (see scoreTag), so the best
// matches — exact / prefix — always survive the cap and sit at the top.
const MAX_RESULTS = 50;

// Split a tag name into the "words" we treat as match boundaries, so a query
// like "music" counts as a strong match inside "Pirate Music" / "battle-music".
const splitWords = (s) => s.split(/[\s\-_/]+/).filter(Boolean);

// Lower score = better match. This is the fix for the original bug: the old code
// sorted alphabetically then sliced to 8, so an exact tag ("music") was pushed
// off the end by all the "<x> music" tags that sort before it. Now exact and
// prefix matches are pinned to the top regardless of the alphabet.
const scoreTag = (name, query) => {
    const n = name.toLowerCase();
    if (n === query) return 0; // exact
    if (n.startsWith(query)) return 1; // prefix
    if (splitWords(n).some((w) => w.startsWith(query))) return 2; // word-start
    if (n.includes(query)) return 3; // substring
    return Infinity; // no match
};

// Wrap every case-insensitive occurrence of the query in <mark> so the user can
// see *why* a tag matched.
const highlightMatch = (name, query) => {
    if (!query) return name;
    const lower = name.toLowerCase();
    const q = query.toLowerCase();
    const parts = [];
    let i = 0;
    while (i < name.length) {
        const idx = lower.indexOf(q, i);
        if (idx === -1) {
            parts.push(name.slice(i));
            break;
        }
        if (idx > i) parts.push(name.slice(i, idx));
        parts.push(
            <mark className="ts-match" key={idx}>
                {name.slice(idx, idx + q.length)}
            </mark>
        );
        i = idx + q.length;
    }
    return parts;
};

const TagSelector = ({ initialTags = [], onExistingTagAdded, onNewTagAdded }) => {
    const [allTags, setAllTags] = useState([]);
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    // -1 = nothing armed (browse mode). 0+ = a row is highlighted. Keeping it
    // unarmed when the query is empty prevents a stray Enter from adding the
    // alphabetically-first tag.
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const itemRefs = useRef([]);

    const baseId = useId();
    const listboxId = `${baseId}-listbox`;
    const optionId = (i) => `${baseId}-opt-${i}`;

    // 1. Fetch tags on mount (with a retry path on failure).
    const fetchTags = async () => {
        setStatus('loading');
        try {
            const tags = await TagService.getAllAvailableTags();
            setAllTags(tags);
            setStatus('ready');
        } catch (err) {
            console.error('Error loading tags:', err);
            setStatus('error');
        }
    };

    useEffect(() => {
        fetchTags();
    }, []);

    // 2. Close the dropdown on outside click.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 3. Build the ranked, de-duplicated result list.
    // Normalize ids to strings on both sides: some hosts (e.g. UploadFileModal)
    // store String(id) while the fetched tag list may carry raw ids — compare
    // like-for-like so already-assigned tags are reliably hidden.
    const assigned = useMemo(() => new Set(initialTags.map(String)), [initialTags]);
    const byId = useMemo(() => indexTagsById(allTags), [allTags]);
    const trimmed = query.trim();
    const q = trimmed.toLowerCase();

    const rankedTags = useMemo(() => {
        const available = allTags.filter((tag) => !assigned.has(String(tag.id)));

        if (!q) {
            // No query → browse mode: order by full hierarchy path so children sit
            // directly under their parent (e.g. "Music", then "Music › Ambient").
            return [...available].sort((a, b) =>
                getFullPathLabel(a.id, byId).localeCompare(getFullPathLabel(b.id, byId))
            );
        }

        return available
            .map((tag) => ({ tag, score: scoreTag(tag.name, q) }))
            .filter(({ score }) => score !== Infinity)
            .sort((a, b) => {
                if (a.score !== b.score) return a.score - b.score;
                // Same tier: shorter names first (closer to the query), then A→Z.
                if (a.tag.name.length !== b.tag.name.length)
                    return a.tag.name.length - b.tag.name.length;
                return a.tag.name.localeCompare(b.tag.name);
            })
            .map(({ tag }) => tag);
        // `byId` is memoized from `allTags`, so listing it changes nothing at
        // runtime — it just satisfies the exhaustive-deps rule, which `CI=true`
        // promotes to a build error.
    }, [allTags, assigned, q, byId]);

    const visibleTags = rankedTags.slice(0, MAX_RESULTS);
    const overflowCount = rankedTags.length - visibleTags.length;

    // Show "Create new" only when the typed text isn't already an exact tag.
    const exactMatch = useMemo(() => allTags.find((t) => t.name.toLowerCase() === q), [allTags, q]);
    const showCreateOption = trimmed.length > 0 && !exactMatch;
    const totalItems = visibleTags.length + (showCreateOption ? 1 : 0);

    // True only when an actual <ul role="listbox"> is in the DOM. Drives the
    // combobox's aria-expanded / aria-controls so they never reference a missing
    // element in the loading / error / empty states.
    const listboxVisible = status === 'ready' && (visibleTags.length > 0 || showCreateOption);
    const alreadyAdded = !!exactMatch && assigned.has(String(exactMatch.id));

    // Keep the highlight inside the (possibly shrunken) list. -1 stays unarmed.
    useEffect(() => {
        setHighlightedIndex((i) => (totalItems === 0 ? -1 : Math.min(i, totalItems - 1)));
    }, [totalItems]);

    // Scroll the highlighted row into view during keyboard navigation.
    useEffect(() => {
        if (isOpen && highlightedIndex >= 0)
            itemRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex, isOpen]);

    // Concise status mirrored into a persistent off-screen live region so screen
    // readers hear result-count / empty / loading changes as the user types.
    let srMessage = '';
    if (status === 'loading') srMessage = 'Loading tags…';
    else if (status === 'error') srMessage = 'Could not load tags.';
    else if (isOpen && !listboxVisible && trimmed)
        srMessage = alreadyAdded ? `“${trimmed}” is already added.` : `No tags match “${trimmed}”.`;
    else if (isOpen && listboxVisible)
        srMessage =
            overflowCount > 0
                ? `Showing ${visibleTags.length} of ${rankedTags.length} tags.`
                : `${visibleTags.length} ${visibleTags.length === 1 ? 'match' : 'matches'} available.`;

    // --- Handlers ---

    // After acting, drop back to unarmed browse mode (query cleared) but keep the
    // dropdown open so the user can add several tags in a row.
    const handleSelect = (tag) => {
        onExistingTagAdded(tag.id);
        setQuery('');
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const handleCreate = () => {
        if (!trimmed) return;
        onNewTagAdded(trimmed);
        setQuery('');
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const handleClear = () => {
        setQuery('');
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!isOpen) setIsOpen(true);
            setHighlightedIndex((prev) => {
                if (totalItems === 0) return -1;
                // Opening, or moving off the unarmed state → land on the first row.
                if (!isOpen || prev < 0) return 0;
                return (prev + 1) % totalItems;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!isOpen) setIsOpen(true);
            setHighlightedIndex((prev) => {
                if (totalItems === 0) return -1;
                if (!isOpen || prev < 0) return totalItems - 1;
                return (prev - 1 + totalItems) % totalItems;
            });
        } else if (e.key === 'Enter') {
            if (!isOpen || totalItems === 0 || highlightedIndex < 0) return;
            e.preventDefault();
            if (highlightedIndex < visibleTags.length) {
                handleSelect(visibleTags[highlightedIndex]);
            } else {
                handleCreate();
            }
        } else if (e.key === 'Escape') {
            // Only swallow Escape while the dropdown is open; when it's closed,
            // let it bubble so a host modal can still close (audit fe-modals-meta-9).
            if (isOpen) {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
            }
        }
    };

    const renderResults = () => {
        if (status === 'loading') {
            return (
                <div className="ts-state" role="status">
                    <FontAwesomeIcon icon={faSpinner} spin /> Loading tags…
                </div>
            );
        }
        if (status === 'error') {
            return (
                <div className="ts-state ts-state--error" role="alert">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>Couldn’t load tags.</span>
                    <button type="button" className="ts-retry" onClick={fetchTags}>
                        Retry
                    </button>
                </div>
            );
        }

        if (!listboxVisible) {
            // Distinguish "this tag is already on the file" from "nothing matches".
            return (
                <div className="ts-state ts-state--empty">
                    <FontAwesomeIcon icon={faTag} />
                    {!trimmed
                        ? 'No tags yet — type to create one.'
                        : alreadyAdded
                          ? `“${trimmed}” is already added.`
                          : `No tags match “${trimmed}”.`}
                </div>
            );
        }

        return (
            <>
                <ul
                    className="ts-list"
                    id={listboxId}
                    role="listbox"
                    aria-label="Tag suggestions"
                    ref={listRef}
                >
                    {visibleTags.map((tag, index) => {
                        const pathLabel = getAncestorPathLabel(tag.id, byId);
                        const multi = hasMultipleParents(tag, byId);
                        return (
                            <li
                                key={tag.id}
                                id={optionId(index)}
                                ref={(el) => (itemRefs.current[index] = el)}
                                role="option"
                                aria-selected={index === highlightedIndex}
                                className={`ts-item ${index === highlightedIndex ? 'is-highlighted' : ''}`}
                                onClick={() => handleSelect(tag)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                title={getFullPathLabel(tag.id, byId)}
                            >
                                <span
                                    className="ts-item-dot"
                                    style={{ background: tag.color || 'var(--qd-text-subtle)' }}
                                    aria-hidden="true"
                                />
                                <span className="ts-item-body">
                                    <span className="ts-item-name">
                                        {highlightMatch(tag.name, trimmed)}
                                    </span>
                                    {pathLabel && (
                                        <span className="ts-item-path">
                                            {pathLabel}
                                            {multi && (
                                                <span className="ts-item-multi" title="Also under other tags"> ⇄</span>
                                            )}
                                        </span>
                                    )}
                                </span>
                            </li>
                        );
                    })}

                    {showCreateOption && (
                        <li
                            id={optionId(visibleTags.length)}
                            ref={(el) => (itemRefs.current[visibleTags.length] = el)}
                            role="option"
                            aria-selected={highlightedIndex === visibleTags.length}
                            className={`ts-item ts-item--create ${
                                highlightedIndex === visibleTags.length ? 'is-highlighted' : ''
                            }`}
                            onClick={handleCreate}
                            onMouseEnter={() => setHighlightedIndex(visibleTags.length)}
                        >
                            <FontAwesomeIcon icon={faPlus} className="ts-item-icon" />
                            <span className="ts-item-name">
                                Create new tag <strong>“{trimmed}”</strong>
                            </span>
                        </li>
                    )}
                </ul>

                <div className="ts-footer">
                    {overflowCount > 0 ? (
                        <span className="ts-footer-count">
                            Showing {visibleTags.length} of {rankedTags.length} — keep typing to
                            narrow
                        </span>
                    ) : (
                        <span className="ts-footer-count">
                            {visibleTags.length}
                            {visibleTags.length === 1 ? ' match' : ' matches'}
                        </span>
                    )}
                    <span className="ts-footer-keys" aria-hidden="true">
                        <kbd>↑</kbd>
                        <kbd>↓</kbd> navigate
                        <kbd>↵</kbd> select
                        <kbd>esc</kbd> close
                    </span>
                </div>
            </>
        );
    };

    return (
        <div className="tag-selector" ref={containerRef}>
            <label className="ts-label" htmlFor={`${baseId}-input`}>
                Add Tag
            </label>

            <div className="ts-input-wrapper">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="ts-search-icon" />
                <input
                    id={`${baseId}-input`}
                    ref={inputRef}
                    type="text"
                    className="ts-input"
                    placeholder="Search or create a tag…"
                    value={query}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={isOpen && listboxVisible}
                    aria-controls={isOpen && listboxVisible ? listboxId : undefined}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        isOpen && listboxVisible && highlightedIndex >= 0
                            ? optionId(Math.min(highlightedIndex, totalItems - 1))
                            : undefined
                    }
                    onChange={(e) => {
                        const value = e.target.value;
                        setQuery(value);
                        setIsOpen(true);
                        // Arm the first match while typing; stay unarmed in browse mode.
                        setHighlightedIndex(value.trim() ? 0 : -1);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />
                {query && (
                    <button
                        type="button"
                        className="ts-clear"
                        onClick={handleClear}
                        aria-label="Clear search"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>

            {isOpen && <div className="ts-dropdown">{renderResults()}</div>}

            {/* Persistent off-screen live region — always mounted so polite
                announcements fire reliably as results change. */}
            <div className="ts-sr-only" role="status" aria-live="polite">
                {srMessage}
            </div>
        </div>
    );
};

export default TagSelector;
